import { useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useQuery, useQueryClient } from 'react-query';
import { deleteBudgetController, getBudgetController } from '../../controller';
import { Budget } from '../../../../../@types/budget';

export const useBudgetData = () => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  const cache = useRef<{ [key: string]: Budget[] }>({});

  // Query para listagem principal
  const {
    data: budgets,
    isFetching: isFetchingRepairs,
    isLoading: isLoadingBudgets,
    refetch,
  } = useQuery({
    queryKey: ['budgets'],
    queryFn: async () => {
      const res = await getBudgetController();
      return res || [];
    },
    staleTime: 1000 * 60,
  });

  // Query para busca por query
  const {
    data: filteredBudgets = [],
    isFetching: isFetchingFiltered,
    refetch: refetchFilteredSearch,
  } = useQuery({
    queryKey: ['filteredBudgets', debouncedSearchQuery],
    queryFn: async () => {
      if (cache.current[debouncedSearchQuery]) {
        return cache.current[debouncedSearchQuery];
      }

      const res = await getBudgetController(debouncedSearchQuery);

      const data = res || [];

      cache.current[debouncedSearchQuery] = data;

      return data;
    },
    enabled: debouncedSearchQuery.trim().length > 0,
    staleTime: 1000 * 60,
  });

  const deleteRepair = async (id_budget: string) => {
    try {
      await deleteBudgetController(id_budget);

      // Atualiza cache da lista principal
      queryClient.setQueryData<Budget[]>(['budgets'], (oldData = []) =>
        oldData.filter(budget => budget.id_budget !== id_budget),
      );

      // Atualiza cache da lista filtrada
      queryClient.setQueryData<Budget[]>(
        ['filteredBudgets', debouncedSearchQuery],
        oldData => {
          if (!oldData) return [];
          return oldData.filter(budget => budget.id_budget !== id_budget);
        },
      );

      if (cache.current[debouncedSearchQuery]) {
        cache.current[debouncedSearchQuery] = cache.current[
          debouncedSearchQuery
        ].filter(budget => budget.id_budget !== id_budget);
      }
    } catch (error) {
      console.error('Erro ao deletar Orçamento:', error);
      throw error;
    }
  };

  return {
    budgets: budgets ?? [],
    filteredBudgets,
    isLoading: isLoadingBudgets || isFetchingFiltered,
    searchQuery,
    setSearchQuery,
    refetch,
    isFetchingRepairs,
    deleteRepair,
    refetchFilteredSearch,
  };
};
